import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { currentPlanOverview } from '../../../../services/settings/settings-dto';
import { SettingsService } from '../../../../services/settings/settings.service';
import { ToastService } from '@storybook/services/toast/toast.service';
import { PdqToastComponent } from '@storybook/components/toast/toast.component';
import { TimeUnits } from '@utils/times-utils';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import { FormControl } from '@angular/forms';
import { environment } from '@environment/environment';
import { ShopService } from '@app/services/shops/shop.service';

@Component({
  selector: 'app-billing-overview',
  templateUrl: './billing-overview.component.html',
  styleUrls: ['./billing-overview.component.scss'],
  standalone: false,
})
export class BillingOverviewComponent implements OnInit, OnDestroy {
  @Output() switchToPlansPage: EventEmitter<string> = new EventEmitter<string>();

  data$ = new BehaviorSubject<currentPlanOverview | null>(null);
  hasError = false;
  enableACHPayments = environment.ENABLE_ACH_PAYMENTS;

  addFundsAmount: number = 0;
  isAddingFunds = false;
  fundingType: 'manual' | 'auto' = 'manual';
  autoTopUpAmount: number = 0;
  autoTopUpThreshold: number = 0;
  isSavingAutoTopUp = false;

  isEditingAutoTopUp = false;
  originalAutoTopUpAmount: number = 0;
  originalAutoTopUpThreshold: number = 0;

  availableBalance: string = '$0.00';
  preloadedAmount: string = '$0.00';
  usedAmount: string = '$0.00';

  addFundsControl = new FormControl(0);
  autoTopUpAmountControl = new FormControl(0);
  autoTopUpThresholdControl = new FormControl(0);

  isSplitBillingPending = false;
  splitBillingPlanName: string = '';
  splitBillingStripeAmount: number = 0;
  splitBillingPlanId: number | null = null;
  splitBillingError: string | null = null;
  private loadOverviewSubscription: Subscription;
  isDeletingPaymentMethod = false;
  isDeletingAchPaymentMethod = false;

  constructor(
    private settingsSrv: SettingsService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private shopService: ShopService,
  ) {}

  ngOnInit(): void {
    this.loadOverviewDetails();

    this.addFundsControl.valueChanges.subscribe((value) => {
      this.addFundsAmount = value || 0;
    });

    this.autoTopUpAmountControl.valueChanges.subscribe((value) => {
      this.autoTopUpAmount = value || 0;
      this.updateEditingState();
    });

    this.autoTopUpThresholdControl.valueChanges.subscribe((value) => {
      this.autoTopUpThreshold = value || 0;
      this.updateEditingState();
    });
  }

  ngOnDestroy(): void {
    if (this.loadOverviewSubscription) this.loadOverviewSubscription.unsubscribe();
  }

  loadOverviewDetails() {
    this.loadOverviewSubscription = this.settingsSrv.getPlanState().subscribe({
      next: (data) => {
        this.data$.next(new currentPlanOverview(data));
        this.hasError = false;

        this.updateComputedValues();
        this.checkSplitBillingSetup();
      },
      error: () => {
        this.data$.next(null);
        this.hasError = true;
      },
    });
  }

  private checkSplitBillingSetup() {
    const data = this.data$.value;

    if (!data?.splitBillingEnabled || !data?.pendingSplitBilling) {
      this.isSplitBillingPending = false;
      this.splitBillingPlanId = null;
      this.splitBillingStripeAmount = 0;
      this.splitBillingPlanName = '';
      this.splitBillingError = null;
      return;
    }

    const isPendingStripe = data.splitBillingStatus === 'PENDING_STRIPE';
    const isSetupFailed = data.splitBillingStatus === 'STRIPE_SETUP_FAILED';

    if (isPendingStripe || isSetupFailed) {
      this.isSplitBillingPending = true;
      this.splitBillingStripeAmount = data.pendingSplitBilling.stripeAmount || 0;
      this.splitBillingPlanName = data.pendingSplitBilling.planName || '';
      this.splitBillingPlanId = data.pendingSplitBilling.planId || null;
      this.splitBillingError = isSetupFailed ? 'Stripe setup failed. Please try again.' : null;
    } else {
      this.isSplitBillingPending = false;
      this.splitBillingPlanId = null;
      this.splitBillingStripeAmount = 0;
      this.splitBillingPlanName = '';
      this.splitBillingError = null;
    }
  }

  private updateComputedValues() {
    const data = this.data$.value;
    this.availableBalance = this.formatAmount(data?.availableCreditsSum || '$0.00');
    this.preloadedAmount = this.formatAmount(data?.preloadedSum || '$0.00');
    this.usedAmount = this.formatAmount(data?.usedSum || '$0.00');
  }

  onGoToPlans() {
    this.switchToPlansPage.emit('plans');
  }

  async onAddPaymentMethod() {
    if (!this.validateShopBillingData()) return;
    const afterClosed$ = await this.settingsSrv.openPaymentMethodSetupWindow('create', this.splitBillingPlanId);
    afterClosed$.subscribe(() => this.onPaymentMethodSetupCompleted());
  }

  async onUpdatePaymentMethod() {
    const afterClosed$ = await this.settingsSrv.openPaymentMethodSetupWindow('update', this.splitBillingPlanId);
    afterClosed$.subscribe(() => this.onPaymentMethodSetupCompleted());
  }

  onDeleteCardPaymentMethod() {
    this._deletePaymentMethod({
      isCard: true,
      dialogTitle: 'Remove Credit Card',
      dialogDescription: 'Are you sure you want to remove this credit card?',
      deleteFn: () => this.settingsSrv.deleteStripePaymentMethod(),
      successMessage: 'Payment method removed',
      errorMessage: 'Failed to remove payment method',
    });
  }

  async onAddACHPaymentMethod() {
    if (!this.validateShopBillingData()) return;
    const afterClosed$ = await this.settingsSrv.openACHPaymentSetupWindow('create', this.splitBillingPlanId);
    afterClosed$.subscribe(() => this.onPaymentMethodSetupCompleted());
  }

  async onUpdateACHPaymentMethod() {
    if (!this.validateShopBillingData()) return;
    const afterClosed$ = await this.settingsSrv.openACHPaymentSetupWindow('update', this.splitBillingPlanId);
    afterClosed$.subscribe(() => this.onPaymentMethodSetupCompleted());
  }

  onDeleteACHPaymentMethod() {
    this._deletePaymentMethod({
      isCard: false,
      dialogTitle: 'Remove ACH bank account',
      dialogDescription: 'Are you sure you want to remove this bank account?',
      deleteFn: () => this.settingsSrv.deleteAchPaymentMethod(),
      successMessage: 'Bank account removed',
      errorMessage: 'Failed to remove bank account',
    });
  }

  private _deletePaymentMethod(config: {
    isCard: boolean;
    dialogTitle: string;
    dialogDescription: string;
    deleteFn: () => Observable<any>;
    successMessage: string;
    errorMessage: string;
  }) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: config.dialogTitle,
        description: config.dialogDescription,
        confirmText: 'Remove',
        cancelText: 'Cancel',
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      if (config.isCard) this.isDeletingPaymentMethod = true;
      else this.isDeletingAchPaymentMethod = true;

      config
        .deleteFn()
        .pipe(
          finalize(() => {
            if (config.isCard) this.isDeletingPaymentMethod = false;
            else this.isDeletingAchPaymentMethod = false;
          }),
        )
        .subscribe({
          next: () => {
            this.loadOverviewDetails();
            this.toastService.show(PdqToastComponent, {
              message: config.successMessage,
              mode: 'success',
              duration: TimeUnits._SECOND_IN_MS * 3,
            });
          },
          error: () => {
            this.toastService.show(PdqToastComponent, {
              message: config.errorMessage,
              mode: 'danger',
              duration: TimeUnits._SECOND_IN_MS * 3,
            });
          },
        });
    });
  }

  onAddFunds() {
    if (this.addFundsAmount > 0 && !this.isAddingFunds) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'Add Funds',
          description: `Are you sure you want to add $${this.addFundsAmount.toFixed(2)} to your account?`,
          confirmText: 'Add Funds',
          cancelText: 'Cancel',
        },
        disableClose: true,
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.confirmAddFunds();
        }
      });
    }
  }

  private confirmAddFunds() {
    this.isAddingFunds = true;

    this.settingsSrv.addFunds(this.addFundsAmount).subscribe({
      next: (response) => {
        this.isAddingFunds = false;

        const addedAmount = this.addFundsAmount;
        this.addFundsAmount = 0;

        this.loadOverviewDetails();

        this.toastService.show(PdqToastComponent, {
          message: `Request to add $${addedAmount.toFixed(2)} has been submitted`,
          mode: 'success',
          duration: TimeUnits._SECOND_IN_MS * 3,
        });
      },
      error: (error) => {
        this.isAddingFunds = false;

        this.toastService.show(PdqToastComponent, {
          message: 'Failed to submit request to add funds',
          mode: 'danger',
          duration: TimeUnits._SECOND_IN_MS * 3,
        });
      },
    });
  }

  setFundingType(type: 'manual' | 'auto') {
    if (this.fundingType !== type) {
      this.isEditingAutoTopUp = false;
      this.fundingType = type;

      if (type === 'auto') {
        this.initializeAutoTopUpValues();
      }
    }
  }

  initializeAutoTopUpValues() {
    const data = this.data$.value;
    if (data) {
      this.autoTopUpAmount = this.parseAmount(data.autoTopUpAmount);
      this.autoTopUpThreshold = this.parseAmount(data.autoTopUpThreshold);
      this.originalAutoTopUpAmount = this.autoTopUpAmount;
      this.originalAutoTopUpThreshold = this.autoTopUpThreshold;

      this.autoTopUpAmountControl.setValue(this.autoTopUpAmount);
      this.autoTopUpThresholdControl.setValue(this.autoTopUpThreshold);
    }
  }

  parseAmount(amountString: string): number {
    if (!amountString) return 0;
    return parseFloat(amountString.replace('$', '')) || 0;
  }

  cancelEditingAutoTopUp() {
    this.isEditingAutoTopUp = false;
    this.autoTopUpAmount = this.originalAutoTopUpAmount;
    this.autoTopUpThreshold = this.originalAutoTopUpThreshold;

    this.autoTopUpAmountControl.setValue(this.originalAutoTopUpAmount);
    this.autoTopUpThresholdControl.setValue(this.originalAutoTopUpThreshold);
  }

  saveAutoTopUpConfig() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Save Auto Top-up Configuration',
        description: `Are you sure you want to save the auto top-up configuration?\n\nAdd: $${this.autoTopUpAmount.toFixed(2)}\nWhen balance falls under: $${this.autoTopUpThreshold.toFixed(2)}`,
        confirmText: 'Save',
        cancelText: 'Cancel',
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.confirmSaveAutoTopUpConfig();
      }
    });
  }

  private confirmSaveAutoTopUpConfig() {
    this.isSavingAutoTopUp = true;

    this.settingsSrv.saveAutoTopUpConfig(this.autoTopUpAmount, this.autoTopUpThreshold).subscribe({
      next: () => {
        this.toastService.show(PdqToastComponent, {
          message: 'Auto top-up configuration saved successfully',
          mode: 'success',
          duration: TimeUnits._SECOND_IN_MS * 3,
        });
        this.originalAutoTopUpAmount = this.autoTopUpAmount;
        this.originalAutoTopUpThreshold = this.autoTopUpThreshold;
        this.isEditingAutoTopUp = false;
        this.isSavingAutoTopUp = false;

        this.autoTopUpAmountControl.setValue(this.autoTopUpAmount);
        this.autoTopUpThresholdControl.setValue(this.autoTopUpThreshold);

        this.loadOverviewDetails();
      },
      error: (error) => {
        this.toastService.show(PdqToastComponent, {
          message: 'Failed to save auto top-up configuration',
          mode: 'danger',
          duration: TimeUnits._SECOND_IN_MS * 3,
        });
        this.isSavingAutoTopUp = false;
        console.error('Error saving auto top-up config:', error);
      },
    });
  }

  updateEditingState() {
    // Check if any value has changed from original AND both values are non-negative
    const hasChanges =
      this.autoTopUpAmount !== this.originalAutoTopUpAmount ||
      this.autoTopUpThreshold !== this.originalAutoTopUpThreshold;

    const bothValuesValid = this.autoTopUpAmount >= 0 && this.autoTopUpThreshold >= 0;

    this.isEditingAutoTopUp = hasChanges && bothValuesValid;
  }

  private formatAmount(amount: string): string {
    if (!amount || amount === '$0.00') return '$0.00';

    const numericValue = parseFloat(amount.replace('$', ''));

    return '$' + numericValue.toFixed(2);
  }

  get isAddFundsDisabled(): boolean {
    return (this.addFundsControl.value || 0) <= 0 || this.isAddingFunds;
  }

  onAmountKeyup(event: any) {
    const value = parseFloat(event.target.value) || 0;
    this.addFundsAmount = value;
    this.addFundsControl.setValue(value);
  }

  onAutoTopUpAmountKeyup(event: any) {
    const value = parseFloat(event.target.value) || 0;
    this.autoTopUpAmount = value;
    this.autoTopUpAmountControl.setValue(value);
    this.updateEditingState();
  }

  onAutoTopUpThresholdKeyup(event: any) {
    const value = parseFloat(event.target.value) || 0;
    this.autoTopUpThreshold = value;
    this.autoTopUpThresholdControl.setValue(value);
    this.updateEditingState();
  }

  private onPaymentMethodSetupCompleted() {
    if (!this.isSplitBillingPending || !this.splitBillingPlanId) {
      this.loadOverviewDetails();
      return;
    }

    this.settingsSrv.getPlanState().subscribe({
      next: (data) => {
        const overview = new currentPlanOverview(data);
        const hasPaymentMethod = !!(overview.creditCardData || overview.achBankAccountData);

        if (!hasPaymentMethod) {
          this.loadOverviewDetails();
          return;
        }

        this.settingsSrv.confirmSplitBillingStripePayment(this.splitBillingPlanId).subscribe({
          next: () => {
            this.isSplitBillingPending = false;
            this.splitBillingPlanId = null;
            this.loadOverviewDetails();
            this.shopService.refreshShop();
            this.toastService.show(PdqToastComponent, {
              message: 'Split billing plan activated successfully',
              mode: 'success',
              duration: TimeUnits._SECOND_IN_MS * 3,
            });
          },
          error: () => {
            this.loadOverviewDetails();
            this.toastService.show(PdqToastComponent, {
              message: 'Payment method saved. Plan activation will be completed shortly.',
              mode: 'info',
              duration: TimeUnits._SECOND_IN_MS * 5,
            });
          },
        });
      },
      error: () => {
        this.loadOverviewDetails();
      },
    });
  }

  private validateShopBillingData(): boolean {
    const shop = this.shopService.currentShopValue;
    if (shop?.metadata?.email) return true;

    this.toastService.show(PdqToastComponent, {
      message: 'Your store is missing required billing information (email). Please fill it in.',
      mode: 'danger',
      duration: TimeUnits._SECOND_IN_MS * 5,
    });
    return false;
  }
}
